import { compareRolePermissions, PERMISSION_GROUPS, type Role } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { HistoryButton } from '../../components/History';
import { api } from '../../lib/api';
import { plural } from '../../lib/format';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Checkbox, Field, Input, Select, Textarea } from '../../ui/form';
import { Panel } from '../../ui/layout';

function RoleDialog({ role, onClose }: { role: Role | 'new'; onClose: () => void }) {
  const qc = useQueryClient();
  const isNew = role === 'new';
  const [name, setName] = useState(isNew ? '' : role.name);
  const [description, setDescription] = useState(isNew ? '' : (role.description ?? ''));
  const [kind, setKind] = useState<'internal' | 'external'>(isNew ? 'internal' : role.kind);
  const [perms, setPerms] = useState<Set<string>>(new Set(isNew ? [] : role.permissions));

  const save = useMutation({
    mutationFn: () => {
      const body = { name, description: description || null, kind, permissions: [...perms] };
      return isNew
        ? api('/admin/roles', { body })
        : api(`/admin/roles/${role.id}`, { method: 'PATCH', body });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin'] });
      qc.invalidateQueries({ queryKey: ['me'] });
      toast(isNew ? 'Роль создана' : 'Роль сохранена');
      onClose();
    },
  });
  const remove = useMutation({
    mutationFn: () => api(`/admin/roles/${(role as Role).id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin'] });
      toast('Роль удалена');
      onClose();
    },
  });

  function toggle(code: string, on: boolean) {
    setPerms((p) => {
      const n = new Set(p);
      if (on) n.add(code);
      else n.delete(code);
      return n;
    });
  }

  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={isNew ? 'Новая роль' : role.name}
      description={
        isNew
          ? 'Набор прав, который назначается сотрудникам на компанию, юрлицо или проект.'
          : (role.description ?? undefined)
      }
      width="lg"
      footer={
        <>
          {!isNew && !role.isSystem ? (
            <Button
              variant="danger"
              className="mr-auto"
              loading={remove.isPending}
              onClick={() => remove.mutate()}
            >
              Удалить роль
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={name.trim().length < 2}
            onClick={() => save.mutate()}
          >
            {isNew ? 'Создать роль' : 'Сохранить'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <div className="grid gap-4 sm:grid-cols-[1fr_220px]">
          <Field label="Название" htmlFor="r-name">
            <Input id="r-name" value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Для кого" htmlFor="r-kind">
            <Select
              id="r-kind"
              value={kind}
              onChange={(e) => setKind(e.target.value as 'internal' | 'external')}
            >
              <option value="internal">Сотрудники компании</option>
              <option value="external">Внешние участники</option>
            </Select>
          </Field>
        </div>
        <Field label="Что делает" htmlFor="r-desc">
          <Textarea
            id="r-desc"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>
        <div className="flex flex-col gap-4">
          <div className="flex items-baseline justify-between">
            <h3 className="text-[15px] font-semibold text-ink">Права</h3>
            <span className="text-[13px] text-ink-2">выбрано {perms.size}</span>
          </div>
          {PERMISSION_GROUPS.map((g) => (
            <fieldset key={g.module} className="rounded-md border border-line p-4">
              <legend className="px-1 text-[13px] font-semibold text-ink">{g.title}</legend>
              <div className="grid gap-2.5 sm:grid-cols-2">
                {g.permissions.map((p) => (
                  <Checkbox
                    key={p.code}
                    label={p.title}
                    checked={perms.has(p.code)}
                    onChange={(e) => toggle(p.code, e.target.checked)}
                  />
                ))}
              </div>
            </fieldset>
          ))}
        </div>
        <ErrorBox error={save.error ?? remove.error} />
      </div>
    </Dialog>
  );
}

/** Сравнение прав двух ролей (АД-15): что есть только у одной и что общее */
function RoleCompare({ roles }: { roles: Role[] }) {
  const [aId, setAId] = useState('');
  const [bId, setBId] = useState('');
  const a = roles.find((r) => r.id === aId);
  const b = roles.find((r) => r.id === bId);
  const result = useMemo(
    () => (a && b ? compareRolePermissions(a.permissions, b.permissions) : null),
    [a, b],
  );
  const options = roles.map((r) => (
    <option key={r.id} value={r.id}>
      {r.name}
    </option>
  ));
  return (
    <Panel title="Сравнить две роли" meta="что есть только у одной, что общее">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Первая роль" htmlFor="cmp-a">
          <Select id="cmp-a" className="h-11" value={aId} onChange={(e) => setAId(e.target.value)}>
            <option value="">Выберите роль</option>
            {options}
          </Select>
        </Field>
        <Field label="Вторая роль" htmlFor="cmp-b">
          <Select id="cmp-b" className="h-11" value={bId} onChange={(e) => setBId(e.target.value)}>
            <option value="">Выберите роль</option>
            {options}
          </Select>
        </Field>
      </div>
      {result && a && b ? (
        <div className="mt-4 flex flex-col gap-4">
          <p className="text-[14px] text-ink-2">
            Только у «{a.name}» — {result.onlyA}, только у «{b.name}» — {result.onlyB}, общих — {result.both}.
          </p>
          {result.groups.length === 0 ? (
            <p className="text-[14px] text-ink-2">У этих ролей нет прав.</p>
          ) : (
            result.groups.map((g) => (
              <div key={g.module} className="overflow-x-auto">
                <h3 className="mb-1 text-[13px] font-semibold text-ink">{g.title}</h3>
                <table className="w-full text-[14px]">
                  <thead>
                    <tr className="text-left text-[12px] text-ink-2">
                      <th className="py-1 pr-3 font-normal">Право</th>
                      <th className="w-20 px-2 py-1 font-normal">Первая</th>
                      <th className="w-20 px-2 py-1 font-normal">Вторая</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.rows.map((row) => (
                      <tr
                        key={row.code}
                        className={`border-t border-line ${row.a === row.b ? '' : 'bg-surface-2'}`}
                      >
                        <td className="py-1.5 pr-3">{row.title}</td>
                        <td className="px-2 py-1.5">{row.a ? 'есть' : '—'}</td>
                        <td className="px-2 py-1.5">{row.b ? 'есть' : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))
          )}
        </div>
      ) : (
        <p className="mt-3 text-[14px] text-ink-2">Выберите две роли, чтобы увидеть различия в правах.</p>
      )}
    </Panel>
  );
}

export function RolesPage() {
  const roles = useQuery({ queryKey: ['admin', 'roles'], queryFn: () => api<Role[]>('/admin/roles') });
  const [editing, setEditing] = useState<Role | 'new' | null>(null);
  const internal = roles.data?.filter((r) => r.kind === 'internal') ?? [];
  const external = roles.data?.filter((r) => r.kind === 'external') ?? [];

  const list = (items: Role[]) => (
    <ul>
      {items.map((r) => (
        <li
          key={r.id}
          className="flex flex-col gap-2 border-t border-line px-5 py-3 first:border-0 sm:flex-row sm:items-start sm:gap-4"
        >
          <button type="button" onClick={() => setEditing(r)} className="flex-1 text-left">
            <span className="flex flex-wrap items-center gap-2">
              <span className="font-medium text-ink hover:underline">{r.name}</span>
              {!r.isSystem ? <Badge tone="info">своя</Badge> : null}
            </span>
            {r.description ? (
              <span className="mt-0.5 block text-[13px] text-ink-2">{r.description}</span>
            ) : null}
          </button>
          <span className="flex shrink-0 items-center gap-3 text-[13px] text-ink-2">
            <span>{plural(r.permissions.length, 'право', 'права', 'прав')}</span>
            <span>
              {r.memberCount
                ? plural(r.memberCount, 'сотрудник', 'сотрудника', 'сотрудников')
                : 'не назначена'}
            </span>
            <HistoryButton table="roles" rowId={r.id} title={r.name} />
          </span>
        </li>
      ))}
    </ul>
  );

  if (roles.isLoading) return <Spinner />;
  if (roles.error) return <ErrorBox error={roles.error} />;

  return (
    <div className="flex flex-col gap-6">
      <Panel
        title="Роли сотрудников"
        meta="по разделу 4 ТЗ"
        actions={
          <Button
            size="sm"
            variant="primary"
            icon={<Plus className="size-4" />}
            onClick={() => setEditing('new')}
          >
            Новая роль
          </Button>
        }
        bodyClassName="p-0"
      >
        {list(internal)}
      </Panel>
      <Panel
        title="Роли внешних участников"
        meta="поставщики, подрядчики, заказчик, надзоры"
        bodyClassName="p-0"
      >
        {list(external)}
      </Panel>
      <RoleCompare roles={roles.data ?? []} />
      {editing ? (
        <RoleDialog
          key={editing === 'new' ? 'new' : editing.id}
          role={editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}
